import { useEffect, useMemo, useRef, useState } from 'react'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Multi-course Play & Stay — Phase 1 partner picker step.
 *
 * Lets the creator:
 *   - See the 10 nearest courses with tee sheets
 *   - Search for other courses
 *   - Add/remove partners from the package's pool
 *   - Invite a course not on our system (email invite with referral-tagged signup link)
 *
 * Props:
 *   onBack()              — return to the scope gate
 *   onNext(partners)      — proceed to Rules step (Phase 2). partners is an array of full hydrated course objects.
 *   initialPartners       — optional list of already-added partners (course objects)
 */
export default function MultiCoursePartnersStep({ onBack, onNext, initialPartners = [], nextLabel, backLabel }) {
  const { t } = useLanguage()
  const [nearby, setNearby] = useState([])
  const [loadingNearby, setLoadingNearby] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [partners, setPartners] = useState(initialPartners)
  const [invites, setInvites] = useState([])
  const [error, setError] = useState('')

  // Invite modal
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteForm, setInviteForm] = useState({ InvitedCourseName: '', InvitedEmail: '', InvitedMessage: '' })
  const [inviteSaving, setInviteSaving] = useState(false)
  const [inviteError, setInviteError] = useState('')

  // Load nearby + existing invites on mount
  useEffect(() => {
    (async () => {
      try {
        const [nearbyRes, invitesRes] = await Promise.all([
          tournamentApi.get('/play-stay/partner-courses/nearby'),
          tournamentApi.get('/play-stay/partner-invites'),
        ])
        setNearby(Array.isArray(nearbyRes.data) ? nearbyRes.data : [])
        setInvites(Array.isArray(invitesRes.data) ? invitesRes.data : [])
      } catch (err) {
        setError(err.response?.data?.message || t('sp_failed_load_nearby', 'Failed to load nearby courses'))
      } finally {
        setLoadingNearby(false)
      }
    })()
  }, [])

  // Debounced search
  const searchTimer = useRef(null)
  useEffect(() => {
    const q = searchQuery.trim()
    if (q.length < 2) { setSearchResults([]); return }
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(async () => {
      setSearching(true)
      try {
        const { data } = await tournamentApi.get('/play-stay/partner-courses/search', { params: { q } })
        setSearchResults(Array.isArray(data) ? data : [])
      } catch {
        setSearchResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => clearTimeout(searchTimer.current)
  }, [searchQuery])

  const partnerIds = useMemo(() => new Set(partners.map(p => p.ID_NO)), [partners])

  function togglePartner(course) {
    if (partnerIds.has(course.ID_NO)) {
      setPartners(prev => prev.filter(p => p.ID_NO !== course.ID_NO))
    } else {
      setPartners(prev => [...prev, course])
    }
  }

  async function handleSendInvite() {
    setInviteError('')
    const name = inviteForm.InvitedCourseName.trim()
    const email = inviteForm.InvitedEmail.trim()
    if (!name) { setInviteError('Course name is required'); return }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setInviteError('Valid email is required'); return
    }
    setInviteSaving(true)
    try {
      const { data } = await tournamentApi.post('/play-stay/partner-invites', inviteForm)
      setInvites(prev => [{ ID_NO: data.id, InvitedCourseName: data.InvitedCourseName, InvitedEmail: data.InvitedEmail, Status: 'sent', DOE: new Date().toISOString() }, ...prev])
      setInviteOpen(false)
      setInviteForm({ InvitedCourseName: '', InvitedEmail: '', InvitedMessage: '' })
    } catch (err) {
      setInviteError(err.response?.data?.message || 'Failed to send invite')
    } finally {
      setInviteSaving(false)
    }
  }

  // Split nearby into "in the top-10 list" vs "already selected but not in list" so
  // a partner added via search still shows as a card in the selected view.
  const selectedNotInNearby = useMemo(
    () => partners.filter(p => !nearby.some(n => n.ID_NO === p.ID_NO)),
    [partners, nearby]
  )

  return (
    <div className="card" style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--secondary-950)' }}>{t('sp_partner_courses_h', 'Partner courses')}</h2>
          <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>
            {t('sp_partners_blurb', 'Pick the courses where customers can play rounds. Add at least 2 partners to continue.')}
          </p>
        </div>
        <button onClick={onBack} className="btn btn-outline-dark" style={{ padding: '8px 16px' }}>
          {backLabel || t('sp_back_arrow', '← Back')}
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Search bar */}
      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t('sp_search_course_placeholder', 'Search for a course by name or city...')}
          style={{
            width: '100%', padding: '12px 16px', fontSize: 14,
            border: '1px solid var(--card-border)', borderRadius: 8,
            background: '#fff',
          }}
        />
      </div>

      {/* Search results (shown only when searching) */}
      {searchQuery.trim().length >= 2 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={sectionHeaderStyle}>
            {t('sp_search_results', 'Search results')}{searching ? ` ${t('sp_searching_paren', '(searching...)')}` : ''}
          </h3>
          {searchResults.length === 0 && !searching ? (
            <NoSearchResults query={searchQuery} onInvite={() => {
              setInviteForm(f => ({ ...f, InvitedCourseName: searchQuery.trim() }))
              setInviteOpen(true)
            }} />
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {searchResults.map(c => (
                <CourseCard key={c.ID_NO} course={c} selected={partnerIds.has(c.ID_NO)} onToggle={() => togglePartner(c)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Nearby list (always shown when not searching OR when search has no results) */}
      {searchQuery.trim().length < 2 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={sectionHeaderStyle}>
            {t('sp_nearby_courses', 'Nearby courses')} {loadingNearby ? t('sp_loading_paren', '(loading...)') : nearby.length ? `(${t('sp_top_n_by_distance', 'top')} ${nearby.length} ${t('sp_by_distance', 'by distance')})` : ''}
          </h3>
          {!loadingNearby && nearby.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', background: '#fafafa', borderRadius: 10, border: '1px solid var(--card-border)' }}>
              {t('sp_no_nearby_courses', 'No other courses with tee sheets found nearby. Use search or invite a course below.')}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {nearby.map(c => (
                <CourseCard key={c.ID_NO} course={c} selected={partnerIds.has(c.ID_NO)} onToggle={() => togglePartner(c)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Extras: selected partners that came from search (not in the nearby list) */}
      {selectedNotInNearby.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={sectionHeaderStyle}>{t('sp_added_from_search', 'Added from search')}</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {selectedNotInNearby.map(c => (
              <CourseCard key={c.ID_NO} course={c} selected onToggle={() => togglePartner(c)} />
            ))}
          </div>
        </div>
      )}

      {/* Invite link */}
      <div style={{ marginBottom: 20, padding: 16, background: '#f9fafb', border: '1px dashed var(--card-border)', borderRadius: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--secondary-950)' }}>{t('sp_dont_see_course', 'Don\'t see the course you want?')}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {t('sp_invite_course_blurb', 'We\'ll send them an invite with a link to join Play & Stay. You\'ll be credited as the referrer.')}
            </div>
          </div>
          <button onClick={() => setInviteOpen(true)} className="btn btn-outline-dark" style={{ padding: '10px 18px', whiteSpace: 'nowrap' }}>
            {t('sp_invite_a_course', 'Invite a course →')}
          </button>
        </div>
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={sectionHeaderStyle}>{t('sp_your_invites', 'Your invites')}</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {invites.map(inv => (
              <div key={inv.ID_NO} style={{
                padding: '10px 14px', border: '1px solid var(--card-border)', borderRadius: 8,
                background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{inv.InvitedCourseName}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{inv.InvitedEmail}</div>
                </div>
                <InviteStatusBadge status={inv.Status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer: selection count + Next */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderTop: '1px solid var(--card-border)', paddingTop: 20, marginTop: 8,
      }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          <strong style={{ color: partners.length >= 2 ? 'var(--brand-700)' : 'var(--secondary-950)' }}>
            {partners.length} {partners.length !== 1 ? t('sp_partners_selected_plural', 'partners selected') : t('sp_partner_selected_singular', 'partner selected')}
          </strong>
          {partners.length < 2 && ' — ' + t('sp_add_at_least_2', 'add at least 2 to continue')}
        </div>
        <button
          onClick={() => partners.length >= 2 && onNext(partners)}
          disabled={partners.length < 2}
          className="btn btn-primary"
          style={{
            padding: '12px 24px',
            opacity: partners.length < 2 ? 0.5 : 1,
            cursor: partners.length < 2 ? 'not-allowed' : 'pointer',
          }}
        >
          {nextLabel || t('sp_next_rules', 'Next: Rules →')}
        </button>
      </div>

      {/* Invite modal */}
      {inviteOpen && (
        <InviteModal
          form={inviteForm}
          setForm={setInviteForm}
          error={inviteError}
          saving={inviteSaving}
          onClose={() => { setInviteOpen(false); setInviteError('') }}
          onSend={handleSendInvite}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────
//  Subcomponents
// ────────────────────────────────────────────────────────────────────────

function CourseCard({ course, selected, onToggle }) {
  const { t } = useLanguage()
  const rate = course.BaseRatePerRound != null ? `$${Number(course.BaseRatePerRound).toFixed(0)}/${t('sp_round_lower', 'round')}` : '—'
  const dist = course.DistanceKm != null ? `${course.DistanceKm} km` : t('sp_distance_unknown', 'distance unknown')
  return (
    <div style={{
      padding: '14px 16px',
      border: '2px solid ' + (selected ? 'var(--brand-500)' : 'var(--card-border)'),
      borderRadius: 10,
      background: selected ? 'var(--brand-50)' : '#fff',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 15, color: 'var(--secondary-950)' }}>{course.CourseName}</strong>
          {!course.IsPlayStayEnrolled && (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 10,
            }}>
              {t('sp_not_yet_enrolled', 'NOT YET ENROLLED')}
            </span>
          )}
          {course.IsOnIntegolfTeeSheet && (
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 10,
            }}
              title={t('sp_on_integolf_title', 'This course is on the InteGolf tee sheet — bookings route through our system')}>
              {t('sp_on_integolf_caps', 'ON INTEGOLF')}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          {course.City || '-'} &middot; {dist} &middot; {t('sp_base_rate', 'base rate')} {rate}
        </div>
      </div>
      <button
        onClick={onToggle}
        className={selected ? 'btn btn-outline-dark' : 'btn btn-primary'}
        style={{ padding: '8px 16px', minWidth: 90, whiteSpace: 'nowrap' }}
      >
        {selected ? t('sp_added_check', '✓ Added') : t('sp_add_plus', '+ Add')}
      </button>
    </div>
  )
}

function NoSearchResults({ query, onInvite }) {
  const { t } = useLanguage()
  return (
    <div style={{
      padding: 20, textAlign: 'center', background: '#fafafa',
      border: '1px dashed var(--card-border)', borderRadius: 10,
    }}>
      <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12 }}>
        {t('sp_no_courses_match_pre', 'No courses match')} "<strong>{query}</strong>". {t('sp_want_to_invite', 'Want to invite them?')}
      </div>
      <button onClick={onInvite} className="btn btn-outline-dark" style={{ padding: '8px 16px' }}>
        {t('sp_invite_quoted_arrow', 'Invite')} "{query}" →
      </button>
    </div>
  )
}

function InviteStatusBadge({ status }) {
  const { t } = useLanguage()
  const map = {
    sent:       { label: t('sp_invite_sent', 'Sent'),       bg: '#dbeafe', fg: '#1e40af' },
    opened:     { label: t('sp_invite_opened', 'Opened'),     bg: '#ede9fe', fg: '#5b21b6' },
    signed_up:  { label: t('sp_invite_signed_up', 'Signed Up'),  bg: '#d1fae5', fg: '#065f46' },
    expired:    { label: t('sp_invite_expired', 'Expired'),    bg: '#f3f4f6', fg: '#6b7280' },
  }
  const s = map[status] || map.sent
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
      background: s.bg, color: s.fg, padding: '3px 10px', borderRadius: 10,
    }}>
      {s.label.toUpperCase()}
    </span>
  )
}

function InviteModal({ form, setForm, error, saving, onClose, onSend }) {
  const { t } = useLanguage()
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
    }}>
      <div style={{ background: '#fff', borderRadius: 12, maxWidth: 520, width: '100%', padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, color: 'var(--secondary-950)' }}>{t('sp_invite_modal_title', 'Invite a course to Play & Stay')}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 0, fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)', padding: 0, lineHeight: 1 }} aria-label={t('sp_close', 'Close')}>&times;</button>
        </div>

        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {t('sp_invite_modal_blurb', 'We\'ll send them an invite with a link to sign up for Play & Stay. Your course will be credited as the referrer.')}
        </p>

        {error && (
          <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label style={modalLabelStyle}>{t('sp_course_name_required_label', 'Course name *')}</label>
          <input
            type="text" value={form.InvitedCourseName}
            onChange={e => setForm(f => ({ ...f, InvitedCourseName: e.target.value }))}
            placeholder="e.g. Green Hills Golf Club"
            style={modalInputStyle}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={modalLabelStyle}>{t('sp_contact_email_required', 'Contact email *')}</label>
          <input
            type="email" value={form.InvitedEmail}
            onChange={e => setForm(f => ({ ...f, InvitedEmail: e.target.value }))}
            placeholder="manager@greenhills.com"
            style={modalInputStyle}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={modalLabelStyle}>{t('sp_personal_note_optional', 'Personal note (optional)')}</label>
          <textarea
            value={form.InvitedMessage}
            onChange={e => setForm(f => ({ ...f, InvitedMessage: e.target.value }))}
            placeholder={t('sp_invite_note_placeholder', 'Hey, I\'d love to partner on a multi-course package for the Cape Breton tour...')}
            rows={4}
            style={{ ...modalInputStyle, resize: 'vertical', minHeight: 80 }}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-outline-dark" style={{ padding: '10px 20px' }} disabled={saving}>
            {t('cancel', 'Cancel')}
          </button>
          <button onClick={onSend} className="btn btn-primary" style={{ padding: '10px 20px' }} disabled={saving}>
            {saving ? t('sp_sending_ellipsis', 'Sending...') : t('sp_send_invite', 'Send invite')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Style helpers ──
const sectionHeaderStyle = {
  margin: '0 0 12px',
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
}
const modalLabelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--secondary-950)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
}
const modalInputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid var(--card-border)',
  borderRadius: 8,
  background: '#fff',
  fontFamily: 'inherit',
}
